import { motion } from "framer-motion";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Heart, Target, BookOpen, Award } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

const About = () => {
  const { colors, theme } = useTheme();
  
  return (
    <>
      <Navigation />
      <div className="pt-16 min-h-screen transition-colors duration-300" style={{ backgroundColor: colors.background, color: colors.foreground }}>
        <section className="py-16 md:py-20 px-4 relative overflow-hidden">
          {/* Animated background */}
          <div className="absolute inset-0 -z-10">
            <motion.div 
              className="absolute top-20 left-20 w-96 h-96 rounded-full blur-3xl"
              style={{ backgroundColor: `${colors.primary}10` }}
              animate={{ 
                scale: [1, 1.2, 1],
                rotate: [0, 180, 0]
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear"
              }}
            />
            <motion.div 
              className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl"
              style={{ backgroundColor: `${colors.primary}10` }}
              animate={{ 
                scale: [1.2, 1, 1.2],
                rotate: [180, 0, 180]
              }}
              transition={{
                duration: 25,
                repeat: Infinity,
                ease: "linear"
              }}
            />
          </div>

          <div className="max-w-6xl mx-auto">
            {/* About Us Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <Heart className="w-16 h-16 mx-auto mb-6" style={{ color: colors.primary }} />
              <h1 className="text-4xl md:text-5xl font-bold mb-8">
                <span 
                  className="bg-clip-text text-transparent"
                  style={{ background: colors.gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
                >
                  About Us
                </span>
              </h1>
              <p className="text-lg md:text-xl max-w-4xl mx-auto leading-relaxed" style={{ color: colors.secondary }}>
                Agape Entertainment is a full-service creative agency built for storytellers. With expertise across 
                media production, marketing, anti-piracy, and celebrity representation, we deliver seamless experiences 
                from pitch to premiere.
              </p>
              <p className="text-lg md:text-xl max-w-4xl mx-auto leading-relaxed mt-6" style={{ color: colors.secondary }}>
                We blend creativity, strategy, and digital precision to unlock the full potential of every project. 
                Our commitment is to authenticity, innovation, and the timeless love of cinema.
              </p>
            </motion.div>

            {/* Mission Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mb-16"
            >
              <div className="flex flex-col md:flex-row items-center gap-12">
                <div className="md:w-1/2">
                  <Target className="w-16 h-16 mb-6" style={{ color: colors.primary }} />
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    <span 
                      className="bg-clip-text text-transparent"
                      style={{ background: colors.gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
                    >
                      Our Mission
                    </span>
                  </h2>
                  <p className="text-lg leading-relaxed mb-6" style={{ color: colors.secondary }}>
                    Our mission is to empower filmmakers, creators, and media brands with intelligent strategies in 
                    production, marketing, and talent management. We combine data, creativity, and technology to build 
                    sustainable success in the dynamic world of entertainment.
                  </p>
                  <p className="text-lg leading-relaxed" style={{ color: colors.secondary }}>
                    At Agape, we strive to amplify voices, nurture stories, and champion cinematic excellence—one frame at a time.
                  </p>
                </div>
                <motion.div 
                  className="md:w-1/2"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <div 
                    className="p-8 rounded-lg backdrop-blur-sm border"
                    style={{ 
                      backgroundColor: theme === 'light' ? colors.card : `${colors.primary}20`,
                      borderColor: `${colors.border}60`
                    }}
                  >
                    <div className="grid grid-cols-2 gap-6">
                      {[
                        { icon: Award, title: "Excellence", desc: "Cinematic quality" },
                        { icon: Heart, title: "Passion", desc: "Love for storytelling" },
                        { icon: Target, title: "Strategy", desc: "Data-driven approach" },
                        { icon: BookOpen, title: "Innovation", desc: "Creative solutions" }
                      ].map((item, index) => (
                        <div key={index} className="text-center">
                          <item.icon className="w-8 h-8 mx-auto mb-2" style={{ color: colors.primary }} />
                          <h4 className="font-semibold mb-1" style={{ color: colors.primary }}>{item.title}</h4>
                          <p className="text-sm" style={{ color: colors.secondary }}>{item.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Our Story Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="flex flex-col md:flex-row-reverse items-center gap-12">
                <div className="md:w-1/2">
                  <BookOpen className="w-16 h-16 mb-6" style={{ color: colors.primary }} />
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    <span 
                      className="bg-clip-text text-transparent"
                      style={{ background: colors.gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
                    >
                      Our Story
                    </span>
                  </h2>
                  <p className="text-lg leading-relaxed mb-6" style={{ color: colors.secondary }}>
                    Founded in 2016, Agape began with a simple mission: to support those who bring stories to life. 
                    What started with celebrity management soon evolved into a full-fledged creative house—spanning 
                    production, distribution, strategy, and beyond.
                  </p>
                  <p className="text-lg leading-relaxed" style={{ color: colors.secondary }}>
                    With every project, we infuse heart, hustle, and high standards. We protect talent, promote stories, 
                    and empower creators through thoughtful execution and deep industry know-how. We are more than a 
                    company—we are partners in your creative journey.
                  </p>
                </div>
                <motion.div 
                  className="md:w-1/2"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <div 
                    className="p-8 rounded-lg backdrop-blur-sm border"
                    style={{ 
                      backgroundColor: theme === 'light' ? colors.card : `${colors.primary}20`,
                      borderColor: `${colors.border}60`
                    }}
                  >
                    <div className="space-y-6">
                      <div className="flex items-center gap-4">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: colors.primary }}></div>
                        <div>
                          <h4 className="font-semibold" style={{ color: colors.primary }}>2016 - The Beginning</h4>
                          <p style={{ color: colors.secondary }}>Started with celebrity management</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: colors.primary }}></div>
                        <div>
                          <h4 className="font-semibold" style={{ color: colors.primary }}>Evolution</h4>
                          <p style={{ color: colors.secondary }}>Expanded to full-service creative house</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: colors.primary }}></div>
                        <div>
                          <h4 className="font-semibold" style={{ color: colors.primary }}>Today</h4>
                          <p style={{ color: colors.secondary }}>Partners in creative journeys</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default About;